import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import config from '../../config'
import PageHeader from '../Elements/UI/PageHeader'
import EmptyState from '../Elements/UI/EmptyState'
import FeedSkeleton from '../Elements/UI/FeedSkeleton'
import PaymentPlan from './SmallComponents/PaymentPlan'
import PaymentDueAlert from '../Elements/UI/PaymentDueAlert'
import {
  normalizeFlat,
  buildPaymentPlan,
  hasExtraFlat,
  formatINR,
  formatDate,
  DEMO_MODE
} from '../../features/payments/dummyPayments'

function PaymentDetails({ userAccess = [] }) {
  const isAdmin = Array.isArray(userAccess) && userAccess.includes('ListUser')

  const [loading, setLoading] = useState(true)
  const [myFlat, setMyFlat] = useState(null)
  const [flats, setFlats] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  // bumped after a receipt upload so the due alert re-evaluates
  const [receiptVersion, setReceiptVersion] = useState(0)

  useEffect(() => {
    const token = localStorage.getItem('token')
    const headers = { Authorization: token }

    const load = async () => {
      try {
        if (isAdmin) {
          const res = await axios.get(`${config.apiUrl}/flat-details/getAll?page=1&limit=500`, { headers })
          const list = (res.data?.data || []).map(normalizeFlat).filter(hasExtraFlat)
          setFlats(list)
        } else {
          const res = await axios.get(`${config.apiUrl}/flat-details`, { headers })
          setMyFlat(normalizeFlat(res.data?.data))
        }
      } catch (err) {
        console.error('Failed to load flat details', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAdmin])

  const selectedFlat = flats.find(f => f.id === selectedId)

  return (
    <div id="appCapsule">
      <div className="section">
        <div className="bl-container px-0">
          <PageHeader
            title={isAdmin ? 'Members’ payment details' : 'Details of payment'}
            subtitle={isAdmin
              ? 'Extra flat (Scheme II) dues and uploaded receipts'
              : 'Your extra flat area charges, due dates and receipts'}
          />

          {DEMO_MODE && (
            <div className="bl-demo-note" role="note">
              <ion-icon name="flask-outline" aria-hidden="true"></ion-icon>
              Demo data: amounts, rate and due dates are sample values for testing.
            </div>
          )}

          {loading && <FeedSkeleton count={2} label="Loading payment details" />}

          {/* ----- Member view ----- */}
          {!loading && !isAdmin && (
            myFlat && hasExtraFlat(myFlat) ? (
              <>
                <PaymentDueAlert key={receiptVersion} flat={myFlat} showLink={false} />
                <PaymentPlan flat={myFlat} onReceiptUploaded={() => setReceiptVersion(v => v + 1)} />
              </>
            ) : (
              <EmptyState
                icon="wallet-outline"
                title="No payments due"
                text="Your flat has no extra area charges under Scheme II."
              />
            )
          )}

          {/* ----- Admin view ----- */}
          {!loading && isAdmin && !selectedFlat && (
            flats.length === 0 ? (
              <EmptyState
                icon="wallet-outline"
                title="No extra flat payments"
                text="No members have an extra flat under Scheme II."
              />
            ) : (
              <ul className="bl-pay-members">
                {flats.map(flat => {
                  const plan = buildPaymentPlan(flat)
                  const next = plan.nextDue
                  const toVerify = plan.instalments.some(x => x.status === 'review')
                  return (
                    <li key={flat.id}>
                      <button
                        type="button"
                        className="card bl-pay-member card-interactive"
                        onClick={() => setSelectedId(flat.id)}
                      >
                        <span className="bl-avatar bl-avatar--lg" aria-hidden="true">
                          {(flat.name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('')}
                        </span>
                        <span className="bl-pay-member__text">
                          <span className="bl-pay-member__name">{flat.name || 'Member'}</span>
                          <span className="bl-pay-member__meta">
                            {flat.room} · {plan.extraAreaSqft} sq.ft · {formatINR(plan.total)}
                          </span>
                        </span>
                        <span className="bl-pay-member__status">
                          {next ? (
                            <span className={`bl-chip ${next.status === 'overdue' ? 'bl-chip--danger' : 'bl-chip--warning'}`}>
                              {next.status === 'overdue' ? 'Overdue' : `Due ${formatDate(next.dueDate)}`}
                            </span>
                          ) : toVerify ? (
                            <span className="bl-chip bl-chip--info">Receipt to verify</span>
                          ) : (
                            <span className="bl-chip bl-chip--success">Up to date</span>
                          )}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )
          )}

          {!loading && isAdmin && selectedFlat && (
            <>
              <button type="button" className="btn btn-link px-0 mb-2" onClick={() => setSelectedId(null)}>
                <ion-icon name="chevron-back-outline" aria-hidden="true"></ion-icon>
                All members
              </button>
              <div className="bl-pay-member__head">
                <h2 className="h3 mb-0">{selectedFlat.name}</h2>
                <span className="text-muted small">Flat {selectedFlat.room}</span>
              </div>
              <PaymentPlan flat={selectedFlat} canUpload={false} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = ({ auth }) => ({
  userAccess: auth.userAccess
})

export default connect(mapStateToProps)(PaymentDetails)
